<script setup lang="ts">
import DataTable from 'primevue/datatable';
import type { DataTableSortEvent } from 'primevue/datatable';
import Column from 'primevue/column';
import Skeleton from 'primevue/skeleton';
import Avatar from 'primevue/avatar';
import SilkIcon from '~/components/icons/SilkIcon.vue';
import cdnAssetPath from '~/functions/cdnAssetPath';
import { formatDateString } from '~/functions/parseDateString';
import type { TournamentGamesProps, TournamentGamesEmits } from './TournamentGamesProps';
import { getGroundName, getGroundDirectionsUrl, getClubName, getClubDirectionsUrl } from './tournamentGameHelpers';
import type { Game } from '~/types/types';

const props = defineProps<TournamentGamesProps>();
const emit = defineEmits<TournamentGamesEmits>();

const localExpandedRows = defineModel<Record<string, boolean>>('expandedRows', { default: () => ({}) });

// Handle row click to toggle expansion
function handleRowClick(event: { data: Game }) {
   const rowId = event.data.id;
   if (localExpandedRows.value[rowId]) {
      delete localExpandedRows.value[rowId];
   } else {
      localExpandedRows.value[rowId] = true;
   }
}


function formatTime(timeString: string | null): string {
   if (!timeString) {
      return '-';
   }
   return timeString;
}

function getPlayerPhotoUrl(photo: string | null | undefined): string {
   if (!photo) return '';
   if (photo.startsWith('http://') || photo.startsWith('https://')) {
      return photo;
   }
   return cdnAssetPath(photo);
}

function handleSort(event: DataTableSortEvent) {
   const field = typeof event.sortField === 'string' ? event.sortField : undefined;
   const order = event.sortOrder === 1 ? 'asc' : event.sortOrder === -1 ? 'desc' : undefined;
   if (field) {
      emit('sort', { field, order });
   }
}
</script>

<template>
   <DataTable
      v-model:expandedRows="localExpandedRows"
      :value="games"
      dataKey="id"
      stripedRows
      showGridlines
      scrollable
      class="nested-games-table"
      :sortField="sortField"
      :sortOrder="sortOrder === 'asc' ? 1 : sortOrder === 'desc' ? -1 : undefined"
      @sort="handleSort"
      @row-click="handleRowClick"
      removableSort
   >
      <Column field="gameDate" header="Date" sortable style="width: 140px">
         <template #body="slotProps">
            <Skeleton v-if="isLoading" width="80%" height="1.2rem" />
            <template v-else>{{ formatDateString(slotProps.data.gameDate) }}</template>
         </template>
      </Column>

      <Column field="time" header="Time" sortable style="width: 80px">
         <template #body="slotProps">
            <Skeleton v-if="isLoading" width="60%" height="1.2rem" />
            <span v-else class="whitespace-nowrap">
               {{ formatTime(slotProps.data.time) }}
            </span>
         </template>
      </Column>

      <Column field="team1Name" header="Team One" sortable>
         <template #body="slotProps">
            <Skeleton v-if="isLoading" width="70%" height="1.2rem" />
            <div v-else class="flex items-center justify-center gap-2">
               <SilkIcon
                  v-if="slotProps.data.teams.team1.shirtColors"
                  :primary-color="slotProps.data.teams.team1.shirtColors.primary"
                  :secondary-color="slotProps.data.teams.team1.shirtColors.secondary"
                  size="20px"
               />
               <span
                  class="font-medium uppercase"
                  :class="{ 'text-green-700': slotProps.data.score.winnerIndex === 1 }"
               >
                  {{ slotProps.data.teams.team1.teamName }}
                  <span v-if="slotProps.data.teams.team1.hcp" class="text-xs font-normal text-gray-500">(HCP: {{ slotProps.data.teams.team1.hcp }})</span>
               </span>
            </div>
         </template>
      </Column>

      <Column header="Score" style="width: 100px">
         <template #body="slotProps">
            <Skeleton v-if="isLoading" width="50px" height="1.2rem" />
            <div v-else class="flex items-center gap-2">
               <span
                  class="inline-flex size-6 items-center justify-center rounded text-sm font-bold text-white"
                  :class="slotProps.data.score.winnerIndex === 1 ? 'bg-green-600' : 'bg-gray-400'"
               >
                  {{ slotProps.data.score.gameScore1 ?? '-' }}
               </span>
               <span
                  class="inline-flex size-6 items-center justify-center rounded text-sm font-bold text-white"
                  :class="slotProps.data.score.winnerIndex === 2 ? 'bg-green-600' : 'bg-gray-400'"
               >
                  {{ slotProps.data.score.gameScore2 ?? '-' }}
               </span>
            </div>
         </template>
      </Column>

      <Column field="team2Name" header="Team Two" sortable>
         <template #body="slotProps">
            <Skeleton v-if="isLoading" width="70%" height="1.2rem" />
            <div v-else class="flex items-center justify-center gap-2">
               <SilkIcon
                  v-if="slotProps.data.teams.team2.shirtColors"
                  :primary-color="slotProps.data.teams.team2.shirtColors.primary"
                  :secondary-color="slotProps.data.teams.team2.shirtColors.secondary"
                  size="20px"
               />
               <span
                  class="font-medium uppercase"
                  :class="{ 'text-green-700': slotProps.data.score.winnerIndex === 2 }"
               >
                  {{ slotProps.data.teams.team2.teamName }}
                  <span v-if="slotProps.data.teams.team2.hcp" class="text-xs font-normal text-gray-500">(HCP: {{ slotProps.data.teams.team2.hcp }})</span>
               </span>
            </div>
         </template>
      </Column>

      <Column field="ground" header="Field" sortable style="width: 120px">
         <template #body="slotProps">
            <Skeleton v-if="isLoading" width="60%" height="1.2rem" />
            <template v-else>{{ getGroundName(slotProps.data.ground) }}</template>
         </template>
      </Column>

      <Column v-if="!isLoading" style="width: 3rem">
         <template #body="slotProps">
            <i
               class="pi pi-chevron-down text-gray-400 transition-transform duration-200"
               :class="{ 'rotate-180': localExpandedRows[slotProps.data.id] }"
            ></i>
         </template>
      </Column>

      <!-- Game Expansion Template -->
      <template #expansion="slotProps">
         <div class="grid grid-cols-1 gap-3 md:grid-cols-3">
            <!-- Team 1 -->
            <div class="bg-gray-50 p-6">
               <div class="mb-3 text-center">
                  <div class="flex items-center justify-center gap-2">
                     <SilkIcon
                        v-if="slotProps.data.teams.team1.shirtColors"
                        :primary-color="slotProps.data.teams.team1.shirtColors.primary"
                        :secondary-color="slotProps.data.teams.team1.shirtColors.secondary"
                        size="24px"
                     />
                     <h4 class="text-sm font-bold uppercase text-gray-900">
                        {{ slotProps.data.teams.team1.teamName }}
                     </h4>
                  </div>
                  <span
                     class="mt-1 inline-flex size-8 items-center justify-center rounded text-lg font-bold text-white"
                     :class="slotProps.data.score.winnerIndex === 1 ? 'bg-green-600' : 'bg-gray-400'"
                  >
                     {{ slotProps.data.score.gameScore1 ?? '-' }}
                  </span>
               </div>
               <ul class="space-y-2 text-sm text-gray-700">
                  <li
                     v-for="(player, playerIndex) in slotProps.data.teams.team1.players"
                     :key="playerIndex"
                     class="flex items-center justify-between"
                  >
                     <div class="flex items-center gap-2">
                        <Avatar
                           v-if="player.photo"
                           :image="getPlayerPhotoUrl(player.photo)"
                           :title="player.country ? `${player.player} (${player.country})` : player.player"
                           shape="circle"
                           size="normal"
                           :pt="{ image: { class: 'object-cover' } }"
                        />
                        <Avatar
                           v-else
                           icon="pi pi-user"
                           :title="player.player"
                           shape="circle"
                           size="normal"
                           class="bg-gray-200"
                        />
                        <span>{{ player.player || '-' }}</span>
                     </div>
                     <span class="text-gray-500">({{ player.hcp || '-' }})</span>
                  </li>
               </ul>
            </div>

            <!-- Team 2 -->
            <div class="bg-gray-50 p-6">
               <div class="mb-3 text-center">
                  <div class="flex items-center justify-center gap-2">
                     <SilkIcon
                        v-if="slotProps.data.teams.team2.shirtColors"
                        :primary-color="slotProps.data.teams.team2.shirtColors.primary"
                        :secondary-color="slotProps.data.teams.team2.shirtColors.secondary"
                        size="24px"
                     />
                     <h4 class="text-sm font-bold uppercase text-gray-900">
                        {{ slotProps.data.teams.team2.teamName }}
                     </h4>
                  </div>
                  <span
                     class="mt-1 inline-flex size-8 items-center justify-center rounded text-lg font-bold text-white"
                     :class="slotProps.data.score.winnerIndex === 2 ? 'bg-green-600' : 'bg-gray-400'"
                  >
                     {{ slotProps.data.score.gameScore2 ?? '-' }}
                  </span>
               </div>
               <ul class="space-y-2 text-sm text-gray-700">
                  <li
                     v-for="(player, playerIndex) in slotProps.data.teams.team2.players"
                     :key="playerIndex"
                     class="flex items-center justify-between"
                  >
                     <div class="flex items-center gap-2">
                        <Avatar
                           v-if="player.photo"
                           :image="getPlayerPhotoUrl(player.photo)"
                           :title="player.country ? `${player.player} (${player.country})` : player.player"
                           shape="circle"
                           size="normal"
                           :pt="{ image: { class: 'object-cover' } }"
                        />
                        <Avatar
                           v-else
                           icon="pi pi-user"
                           :title="player.player"
                           shape="circle"
                           size="normal"
                           class="bg-gray-200"
                        />
                        <span>{{ player.player || '-' }}</span>
                     </div>
                     <span class="text-gray-500">({{ player.hcp || '-' }})</span>
                  </li>
               </ul>
            </div>

            <!-- Game Info -->
            <div class="space-y-3 bg-white p-6">
               <!-- Live Stream -->
               <div v-if="slotProps.data.links.livestreamLink" class="flex items-center gap-2">
                  <svg class="size-5 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                     <circle cx="12" cy="12" r="2" />
                     <path d="M16.24 7.76a6 6 0 0 1 0 8.49m-8.48-.01a6 6 0 0 1 0-8.49m11.31-2.82a10 10 0 0 1 0 14.14m-14.14 0a10 10 0 0 1 0-14.14" />
                  </svg>
                  <span class="text-sm text-gray-500">Live Stream:</span>
                  <a
                     :href="slotProps.data.links.livestreamLink"
                     target="_blank"
                     rel="noopener noreferrer"
                     class="inline-flex items-center gap-1 text-sm text-gray-900 underline hover:no-underline"
                  >
                     Go To Live Stream
                     <i class="pi pi-external-link text-xs"></i>
                  </a>
               </div>

               <!-- Highlights -->
               <div v-if="slotProps.data.links.multiMarkerMap" class="flex items-center gap-2">
                  <i class="pi pi-play-circle text-lg text-gray-500"></i>
                  <span class="text-sm text-gray-500">Highlights:</span>
                  <button class="text-sm text-gray-900 underline hover:no-underline">
                     Play Video
                  </button>
               </div>

               <!-- Club -->
               <div v-if="slotProps.data.club" class="flex items-center gap-2.5">
                  <span class="flex size-7 shrink-0 items-center justify-center rounded-full bg-blue-100">
                     <i class="pi pi-building text-xs text-blue-600"></i>
                  </span>
                  <div class="flex flex-wrap items-center gap-x-1">
                     <span class="text-sm text-gray-500">Club:</span>
                     <span class="text-sm text-gray-900">{{ getClubName(slotProps.data.club) }}</span>
                     <a
                        v-if="getClubDirectionsUrl(slotProps.data.club)"
                        :href="getClubDirectionsUrl(slotProps.data.club)!"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="ml-1 inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
                        @click.stop
                     >
                        <i class="pi pi-map-marker text-[10px]"></i>
                        Directions
                     </a>
                  </div>
               </div>

               <!-- Ground -->
               <div v-if="slotProps.data.ground" class="flex items-center gap-2.5">
                  <span class="flex size-7 shrink-0 items-center justify-center rounded-full bg-green-100">
                     <i class="pi pi-flag text-xs text-green-600"></i>
                  </span>
                  <div class="flex flex-wrap items-center gap-x-1">
                     <span class="text-sm text-gray-500">Field:</span>
                     <span class="text-sm text-gray-900">{{ getGroundName(slotProps.data.ground) }}</span>
                     <a
                        v-if="getGroundDirectionsUrl(slotProps.data.ground)"
                        :href="getGroundDirectionsUrl(slotProps.data.ground)!"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="ml-1 inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
                        @click.stop
                     >
                        <i class="pi pi-map-marker text-[10px]"></i>
                        Directions
                     </a>
                  </div>
               </div>

               <!-- Umpires -->
               <div v-if="slotProps.data.officials.umpire1 || slotProps.data.officials.umpire2" class="flex items-center gap-2.5">
                  <span class="flex size-7 shrink-0 items-center justify-center rounded-full bg-purple-100">
                     <i class="pi pi-user text-xs text-purple-600"></i>
                  </span>
                  <div>
                     <span class="text-sm text-gray-500">Umpires:</span>
                     <span class="ml-1 text-sm text-gray-900">
                        {{ [slotProps.data.officials.umpire1, slotProps.data.officials.umpire2].filter(Boolean).join(', ') }}
                     </span>
                  </div>
               </div>

               <!-- Referee -->
               <div v-if="slotProps.data.officials.referee" class="flex items-center gap-2.5">
                  <span class="flex size-7 shrink-0 items-center justify-center rounded-full bg-amber-100">
                     <i class="pi pi-id-card text-xs text-amber-600"></i>
                  </span>
                  <span class="text-sm text-gray-500">Referee:</span>
                  <span class="text-sm text-gray-900">{{ slotProps.data.officials.referee }}</span>
               </div>

               <!-- Red Card -->
               <div v-if="slotProps.data.cards?.redCard" class="flex items-start gap-2">
                  <span class="mt-0.5 inline-block h-5 w-3.5 -rotate-6 rounded-sm bg-red-600 shadow-sm"></span>
                  <div>
                     <span class="text-sm text-gray-500">Red Card:</span>
                     <span class="ml-1 text-sm text-gray-900">{{ slotProps.data.cards.redCard }}</span>
                  </div>
               </div>

               <!-- Yellow Card -->
               <div v-if="slotProps.data.cards?.yellowCard" class="flex items-start gap-2">
                  <span class="mt-0.5 inline-block h-5 w-3.5 -rotate-6 rounded-sm bg-yellow-400 shadow-sm"></span>
                  <div>
                     <span class="text-sm text-gray-500">Yellow Card:</span>
                     <span class="ml-1 text-sm text-gray-900">{{ slotProps.data.cards.yellowCard }}</span>
                  </div>
               </div>

               <!-- Google Maps Link -->
               <div v-if="slotProps.data.links.googleMapsLink" class="flex items-center gap-2">
                  <i class="pi pi-map-marker text-lg text-gray-500"></i>
                  <a
                     :href="slotProps.data.links.googleMapsLink"
                     target="_blank"
                     rel="noopener noreferrer"
                     class="inline-flex items-center gap-1 text-sm text-gray-900 underline hover:no-underline"
                  >
                     View Location
                     <i class="pi pi-external-link text-xs"></i>
                  </a>
               </div>
            </div>
         </div>
      </template>
   </DataTable>
</template>

<style scoped>
:deep(.nested-games-table .p-datatable-tbody td) {
   text-align: center;
   vertical-align: middle;
}

/* Make rows clickable (cursor pointer) */
:deep(.nested-games-table .p-datatable-tbody > tr:not(.p-datatable-row-expansion)) {
   cursor: pointer;
}

/* Remove bottom border on expanded rows for cohesive appearance */
:deep(.nested-games-table .p-datatable-tbody > tr.p-datatable-row-expanded) {
   --p-datatable-body-cell-border-color: transparent;
}
</style>
